import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { supabase } from '../supabaseClient';
import useApi from '../api';
import { useUser } from '../user/UserContext';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from '@/lib/utils';
import { ArrowRight, Building2, Check, Clock, KeyRound, LogOut, Rocket, Users } from 'lucide-react';
import LogoSymbol from '../../logo_symbol.png';

type DecisionState = {
  requires_decision: boolean;
  email_domain?: string | null;
  organization?: {
    id: string;
    name: string;
  } | null;
  access_request_status?: string | null;
};

export default function OrganizationAccessDecision() {
  const navigate = useNavigate();
  const { get, post } = useApi();
  const { onboardingDecision, checkUser } = useUser();
  const [decision, setDecision] = useState<DecisionState | null>(onboardingDecision);
  const [loading, setLoading] = useState<boolean>(false);
  const [hoveredCard, setHoveredCard] = useState<'trial' | 'access' | null>(null);

  const isRequestPending = useMemo(
    () => decision?.access_request_status === 'access_requested',
    [decision]
  );

  const loadDecision = async () => {
    try {
      const response = await get('/users/me/onboarding-decision');
      setDecision(response.data);
      if (!response.data?.requires_decision) {
        await checkUser();
        navigate('/', { replace: true });
      }
    } catch (error: any) {
      toast.error(error?.detail || 'Failed to load onboarding decision state.');
    }
  };

  useEffect(() => {
    loadDecision();
  }, []);

  useEffect(() => {
    setDecision(onboardingDecision);
  }, [onboardingDecision]);

  useEffect(() => {
    if (!isRequestPending) {
      return;
    }

    const interval = setInterval(async () => {
      // Re-run the complete bootstrap after approval so the organization and
      // its onboarding state are resolved before leaving this screen.
      await loadDecision();
    }, 12000);

    return () => clearInterval(interval);
  }, [isRequestPending]);

  const handleStartTrial = async () => {
    setLoading(true);
    try {
      const response = await post('/users/me/onboarding-decision', { action: 'start_new_trial' });
      if (response?.data?.outcome === 'trial_started') {
        await checkUser();
        navigate('/', { replace: true });
        return;
      }
      toast.error('Unable to start a new trial right now.');
    } catch (error: any) {
      toast.error(error?.detail || 'Unable to start a new trial.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async () => {
    setLoading(true);
    try {
      const response = await post('/users/me/onboarding-decision', { action: 'request_access' });
      const result = response?.data;
      if (result?.outcome === 'joined_existing_org') {
        await checkUser();
        navigate('/', { replace: true });
        return;
      }

      if (result?.outcome === 'access_requested' || result?.outcome === 'already_requested') {
        toast.success('Access request sent. We will email you after approval.');
        await loadDecision();
        return;
      }

      toast.error('Unable to request access right now.');
    } catch (error: any) {
      toast.error(error?.detail || 'Unable to request access.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('aimdocAdmin');
    localStorage.removeItem('aimdocOrganization');
    localStorage.removeItem('aimdocOnboardingDecision');
    navigate('/login', { replace: true });
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background text-foreground">
      {/* Subtle background accent */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.08),transparent_60%)]" />
      </div>

      {/* Top bar */}
      <div className="relative z-20 flex items-center justify-between px-6 py-4">
        <a href="/" className="flex items-center">
          <span className="bg-gradient-to-tr from-blue-700 via-blue-400 to-blue-100 bg-clip-text text-2xl font-bold uppercase tracking-tighter text-transparent">
            Aimdoc
          </span>
        </a>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground gap-2"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </Button>
      </div>

      <div className="relative z-10 flex min-h-[calc(100vh-72px)] items-center justify-center px-6 pb-12">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="mb-10 flex flex-col items-center text-center">
            <img src={LogoSymbol} alt="Aimdoc" className="mb-1 h-12 w-12" />
            <h1 className="text-2xl font-semibold tracking-tight">How would you like to get started?</h1>
            <p className="mt-2 max-w-md text-sm text-muted-foreground">
              We found an existing organization matching{' '}
              <span className="font-medium text-foreground">{decision?.email_domain || 'your domain'}</span>.
              Choose how you'd like to proceed.
            </p>
          </div>

          {/* Option cards */}
          <div className="space-y-3">
            {/* Start New Trial */}
            <button
              type="button"
              onClick={handleStartTrial}
              disabled={loading}
              onMouseEnter={() => setHoveredCard('trial')}
              onMouseLeave={() => setHoveredCard(null)}
              className={cn(
                "w-full text-left rounded-xl border p-5 transition-all duration-200 group",
                "hover:border-blue-500/40 hover:bg-blue-500/[0.03]",
                hoveredCard === 'trial' ? "border-blue-500/40 bg-blue-500/[0.03]" : "border-border bg-card",
                loading && "opacity-60 cursor-not-allowed"
              )}
            >
              <div className="flex items-start gap-4">
                <div className={cn(
                  "h-10 w-10 shrink-0 rounded-lg flex items-center justify-center transition-colors",
                  hoveredCard === 'trial'
                    ? "bg-blue-500/15 text-blue-600 dark:text-blue-400"
                    : "bg-muted text-muted-foreground"
                )}>
                  <Rocket className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm">Start a new trial</h3>
                    <ArrowRight className={cn(
                      "h-4 w-4 text-muted-foreground transition-all",
                      hoveredCard === 'trial' && "text-blue-500 translate-x-0.5"
                    )} />
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    Create your own organization with a fresh workspace and full trial access.
                  </p>
                  <div className="mt-3 flex items-center gap-4">
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Check className="h-3.5 w-3.5 text-blue-500" />
                      Own workspace
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Check className="h-3.5 w-3.5 text-blue-500" />
                      Full feature access
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Check className="h-3.5 w-3.5 text-blue-500" />
                      Instant setup
                    </span>
                  </div>
                </div>
              </div>
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 px-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">or</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* Request Access */}
            <button
              type="button"
              onClick={handleRequestAccess}
              disabled={loading || isRequestPending}
              onMouseEnter={() => setHoveredCard('access')}
              onMouseLeave={() => setHoveredCard(null)}
              className={cn(
                "w-full text-left rounded-xl border p-5 transition-all duration-200 group",
                isRequestPending
                  ? "border-amber-500/30 bg-amber-500/[0.03]"
                  : "hover:border-emerald-500/40 hover:bg-emerald-500/[0.03]",
                !isRequestPending && hoveredCard === 'access'
                  ? "border-emerald-500/40 bg-emerald-500/[0.03]"
                  : !isRequestPending ? "border-border bg-card" : "",
                (loading || isRequestPending) && "cursor-not-allowed",
                loading && !isRequestPending && "opacity-60"
              )}
            >
              <div className="flex items-start gap-4">
                <div className={cn(
                  "h-10 w-10 shrink-0 rounded-lg flex items-center justify-center transition-colors",
                  isRequestPending
                    ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                    : hoveredCard === 'access'
                      ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                      : "bg-muted text-muted-foreground"
                )}>
                  {isRequestPending ? <Clock className="h-5 w-5" /> : <KeyRound className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-sm">
                        {isRequestPending ? 'Access request pending' : 'Join existing organization'}
                      </h3>
                      {isRequestPending && (
                        <Badge variant="outline" className="border-amber-500/40 text-amber-600 dark:text-amber-400 text-[10px] h-5">
                          Awaiting approval
                        </Badge>
                      )}
                    </div>
                    {!isRequestPending && (
                      <ArrowRight className={cn(
                        "h-4 w-4 text-muted-foreground transition-all",
                        hoveredCard === 'access' && "text-emerald-500 translate-x-0.5"
                      )} />
                    )}
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    {isRequestPending
                      ? 'Your request has been sent. An admin will review it shortly and you\'ll be notified by email.'
                      : 'Request to join the existing organization. An admin will need to approve your access.'
                    }
                  </p>
                  {!isRequestPending && (
                    <div className="mt-3 flex items-center gap-2">
                      <div className="flex items-center gap-2 px-2.5 py-1 rounded-md bg-muted/50 border border-border/50">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs font-medium">{decision?.organization?.name || 'Organization'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Users className="h-3.5 w-3.5" />
                        Team workspace
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
